import type {
  CareerDetailPageData,
  CareersListPageData,
  CompanyPageData,
  ContactPageData,
  HomePageData,
  NewsDetailPageData,
  NewsListPageData,
  WorkPageData,
} from "@site-types/publicPageData"
import type { SupportedLocale } from "@site-types/content"
import { queryOptions } from "@tanstack/react-query"
import { createServerFn } from "@tanstack/react-start"
import * as z from "zod"

const localeInputSchema = z.object({
  locale: z.enum(["en", "ja"]),
})

const detailInputSchema = localeInputSchema.extend({
  slug: z.string().min(1),
})

const PUBLIC_PAGE_DATA_STALE_MS = 5 * 60 * 1000
const PUBLIC_PAGE_DATA_GC_MS = 30 * 60 * 1000

function serverFnPayload<T>(value: T): never {
  return value as never
}

function publicPageQueryDefaults() {
  return {
    gcTime: PUBLIC_PAGE_DATA_GC_MS,
    refetchOnWindowFocus: false,
    staleTime: PUBLIC_PAGE_DATA_STALE_MS,
  }
}

const getHomePageData = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => localeInputSchema.parse(input))
  .handler(async ({ data }) => {
    const { getHomePageData } = await import("@data/publicPageData.server")
    return serverFnPayload(await getHomePageData(data.locale))
  })

const getWorkPageData = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => localeInputSchema.parse(input))
  .handler(async ({ data }) => {
    const { getWorkPageData } = await import("@data/publicPageData.server")
    return serverFnPayload(await getWorkPageData(data.locale))
  })

const getCompanyPageData = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => localeInputSchema.parse(input))
  .handler(async ({ data }) => {
    const { getCompanyPageData } = await import("@data/publicPageData.server")
    return serverFnPayload(await getCompanyPageData(data.locale))
  })

const getNewsListPageData = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => localeInputSchema.parse(input))
  .handler(async ({ data }) => {
    const { getNewsListPageData } = await import("@data/publicPageData.server")
    return serverFnPayload(await getNewsListPageData(data.locale))
  })

const getNewsDetailPageData = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => detailInputSchema.parse(input))
  .handler(async ({ data }) => {
    const { getNewsDetailPageData } = await import("@data/publicPageData.server")
    return serverFnPayload(await getNewsDetailPageData(data.locale, data.slug))
  })

const getCareersListPageData = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => localeInputSchema.parse(input))
  .handler(async ({ data }) => {
    const { getCareersListPageData } = await import("@data/publicPageData.server")
    return serverFnPayload(await getCareersListPageData(data.locale))
  })

const getCareerDetailPageData = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => detailInputSchema.parse(input))
  .handler(async ({ data }) => {
    const { getCareerDetailPageData } = await import("@data/publicPageData.server")
    return serverFnPayload(await getCareerDetailPageData(data.locale, data.slug))
  })

const getContactPageData = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => localeInputSchema.parse(input))
  .handler(async ({ data }) => {
    const { getContactPageData } = await import("@data/publicPageData.server")
    return serverFnPayload(await getContactPageData(data.locale))
  })

export function homePageQueryOptions(locale: SupportedLocale) {
  return queryOptions({
    ...publicPageQueryDefaults(),
    queryKey: ["public-page-data", "home", locale],
    queryFn: async () => (await getHomePageData({ data: { locale } })) as HomePageData,
  })
}

export function workPageQueryOptions(locale: SupportedLocale) {
  return queryOptions({
    ...publicPageQueryDefaults(),
    queryKey: ["public-page-data", "work", locale],
    queryFn: async () => (await getWorkPageData({ data: { locale } })) as WorkPageData,
  })
}

export function companyPageQueryOptions(locale: SupportedLocale) {
  return queryOptions({
    ...publicPageQueryDefaults(),
    queryKey: ["public-page-data", "company", locale],
    queryFn: async () => (await getCompanyPageData({ data: { locale } })) as CompanyPageData,
  })
}

export function newsListPageQueryOptions(locale: SupportedLocale) {
  return queryOptions({
    ...publicPageQueryDefaults(),
    queryKey: ["public-page-data", "news-list", locale],
    queryFn: async () => (await getNewsListPageData({ data: { locale } })) as NewsListPageData,
  })
}

export function newsDetailPageQueryOptions(locale: SupportedLocale, slug: string) {
  return queryOptions({
    ...publicPageQueryDefaults(),
    queryKey: ["public-page-data", "news-detail", locale, slug],
    queryFn: async () =>
      (await getNewsDetailPageData({ data: { locale, slug } })) as NewsDetailPageData | null,
  })
}

export function careersListPageQueryOptions(locale: SupportedLocale) {
  return queryOptions({
    ...publicPageQueryDefaults(),
    queryKey: ["public-page-data", "careers-list", locale],
    queryFn: async () => (await getCareersListPageData({ data: { locale } })) as CareersListPageData,
  })
}

export function careerDetailPageQueryOptions(locale: SupportedLocale, slug: string) {
  return queryOptions({
    ...publicPageQueryDefaults(),
    queryKey: ["public-page-data", "career-detail", locale, slug],
    queryFn: async () =>
      (await getCareerDetailPageData({ data: { locale, slug } })) as CareerDetailPageData | null,
  })
}

export function contactPageQueryOptions(locale: SupportedLocale) {
  return queryOptions({
    ...publicPageQueryDefaults(),
    queryKey: ["public-page-data", "contact", locale],
    queryFn: async () => (await getContactPageData({ data: { locale } })) as ContactPageData,
  })
}
